export type DataPointDomain =
  | "talotekniikka" // Physical building systems: sensors and confirmed BMS setpoints
  | "palvelut" // External context: weather, electricity price, grid carbon
  | "ennuste" // Predictions by any forecasting model (provenance, not time)
  | "optimointi" // Agent analysis and recipe recommendations
  | "simulointi"; // Hiekkalaatikko what-if counterfactual outputs

export type DataPointNature =
  | "sensor" // Raw physical reading from a sensor or BMS
  | "setpoint" // Target value written to a control system
  | "actuator" // Physical actuator position or command output
  | "status" // On/off, alarm, or mode state signal
  | "calculated" // Derived KPI or agent-computed value
  | "forecast" // Model-generated value (recorded_at may be in the future)
  | "marker"; // Kairos-internal sticky pin (e.g. <family_id>__effective)

export const DATA_POINT_DOMAIN_LABEL: Record<DataPointDomain, string> = {
  talotekniikka: "Talotekniikka",
  palvelut: "Palvelut",
  ennuste: "Ennuste",
  optimointi: "Optimointi",
  simulointi: "Simulointi",
};

// Building-level sources (outdoor temperature, solar radiation, electricity price, …) are
// external context/forecast feeds, not a BMS unit — their group header must name the source
// *kind*, never the bare building name, so it doesn't read as if the building were a sensor.
// `palvelut` and `ennuste` share one label: Signaalit shows one Ulkoiset palvelut group per
// building regardless of which of the two domains a given feed's rows carry — the domain axis
// stays internal classification, not UI chrome (story #882).
export const BUILDING_SOURCE_KIND_LABEL: Record<DataPointDomain, string> = {
  palvelut: "Ulkoiset palvelut",
  ennuste: "Ulkoiset palvelut",
  talotekniikka: "Kiinteistön talotekniikka",
  optimointi: "Kiinteistön optimointi",
  simulointi: "Kiinteistön simulointi",
};

export const DATA_POINT_DOMAIN_COLOR: Record<DataPointDomain, string> = {
  talotekniikka: "#5b8dee",
  palvelut: "#4db6ac",
  ennuste: "#9575cd",
  optimointi: "#4caf82",
  simulointi: "#e879f9",
};

// `forecast`'s label is "Ennustearvo", deliberately distinct from the `ennuste` domain's own
// label — the two axes are independent, but a bare "Ennuste" for both would read as one concept
// in filter chrome that shows nature pills next to domain-derived group headers (story #882).
export const DATA_POINT_NATURE_LABEL: Record<DataPointNature, string> = {
  sensor: "Anturitieto",
  setpoint: "Asetusarvo",
  actuator: "Toimilaite",
  status: "Tilatieto",
  calculated: "Laskettu",
  forecast: "Ennustearvo",
  marker: "Merkki",
};

export const DATA_POINT_NATURE_COLOR: Record<DataPointNature, string> = {
  sensor: "#5b8dee",
  setpoint: "#ff8a65",
  actuator: "#ffa726",
  status: "#78909c",
  calculated: "#f5a623",
  forecast: "#9575cd",
  marker: "#90a4ae",
};

export const DOMAIN_ORDER: DataPointDomain[] = [
  "talotekniikka",
  "palvelut",
  "ennuste",
  "optimointi",
  "simulointi",
];

export const NATURE_ORDER: DataPointNature[] = [
  "sensor",
  "setpoint",
  "actuator",
  "status",
  "calculated",
  "forecast",
];

export interface DataPoint {
  domain: DataPointDomain;
  nature: DataPointNature;
  name: string;
  value: string;
  numeric_value?: number;
  text_value?: string;
  unit?: string;
  severity?: "low" | "medium" | "high";
  recorded_at?: string;
  source?: "manual" | "sensor" | "agent" | "external";
}

export interface ProcessCard {
  id: string;
  zone_id: string | null;
  zone_ids: string[];
  location: string | null;
  name: string;
  equipment_type: string;
  equipment_tag: string;
  icon: string;
  color: string;
  optimization_pct: number;
  optimization_color: string;
  // Whether the autonomous optimization loop may generate recipes for this equipment —
  // distinct from optimization_pct (a display metric). Off by default (opt-in).
  optimization_enabled: boolean;
  kpis: { label: string; value: string }[];
  data_points: DataPoint[];
  live_data_points: DataPoint[];
  state: Record<string, unknown>;
  declared_variants: string[];
  declared_data_points: string[]; // empty = all data points potentially present
  declared_component_order: string[];
  declared_component_subtypes: Record<string, string>;
  declared_stream_component_order: Record<string, string[]>;
}

export interface Zone {
  id: string;
  building_id: string;
  name: string;
  floor: number; // 0 = building-level zone
  icon: string; // vestigial free-form key; display derives from zone_type (see zoneSymbol)
  zone_type: string | null;
  description: string;
  updated_at: string;
}

// Fallback only. The Knowledge Base (zones/<type>/zone.yaml `icon`) is the source of
// truth for zone symbols, surfaced via getKBTypes().zone_types[].icon. This mirror is
// keyed by KB zone-type id and used only before the KB summary has loaded (or if it fails).
export const ZONE_TYPE_ICON: Record<string, string> = {
  office: "💼",
  "open-office": "🏢",
  "meeting-room": "👥",
  "server-room": "🖥️",
  lobby: "🚪",
};

export function zoneSymbol(zoneType: string | null | undefined): string {
  return (zoneType && ZONE_TYPE_ICON[zoneType]) || "📍";
}

// Equipment type vocabulary aligned with building optimization model
export const EQUIPMENT_TYPE_OPTIONS = [
  "air_handling_unit",
  "heat_pump",
  "chiller",
  "hot_water_unit",
  "refrigeration_unit",
  "district_heating_substation",
  "boiler",
  "pump_group",
  "heat_exchanger",
  "electricity_meter",
  "heat_meter",
  "temperature_sensor",
  "chilled_beam",
] as const;

export type EquipmentTypeKey = (typeof EQUIPMENT_TYPE_OPTIONS)[number];

// EquipmentCard is semantically equivalent to ProcessCard — same shape, updated naming
export type EquipmentCard = ProcessCard;

// Equipment↔zone membership: the many-link list is the source of truth. An empty `zone_ids`
// array must not hide a legacy singular `zone_id` (`??` does not catch `[]`).
export function resolvedEquipmentZoneIds(equipment: {
  zone_ids?: readonly string[] | null;
  zone_id?: string | null;
}): string[] {
  if (equipment.zone_ids && equipment.zone_ids.length > 0) {
    return [...equipment.zone_ids];
  }
  return equipment.zone_id ? [equipment.zone_id] : [];
}

// Zone display names an equipment instance serves, resolved against a zone list — empty when
// it serves none (a building-wide item like an electricity meter) or a linked zone id no
// longer resolves against `zones`.
export function servedZoneNames(
  equipment: {
    zone_ids?: readonly string[] | null;
    zone_id?: string | null;
  } | null,
  zones: readonly { id: string; name: string }[],
): string[] {
  if (!equipment) return [];
  const nameById = new Map(zones.map((z) => [z.id, z.name]));
  return resolvedEquipmentZoneIds(equipment)
    .map((id) => nameById.get(id))
    .filter((name): name is string => !!name);
}

export interface SankeyNode {
  id: string;
  label: string;
  x: number;
  y: number;
  height: number;
  color: string;
}

export interface SankeyLink {
  from: string;
  from_y: number;
  to: string;
  to_y: number;
  width: number;
  color: string;
  label?: string;
}

export interface SankeyDiagram {
  nodes: SankeyNode[];
  links: SankeyLink[];
  billing_boundary_x?: number;
  width: number;
  height: number;
}

// ── Scenario simulation ───────────────────────────────────────────────────────

import type { MeasurementStats } from "@/lib/api/building";

export interface ScenarioDefinition {
  id: string;
  name_fi: string;
  description_fi: string;
  // Equipment types that support this capability
  supported_equipment_types: string[];
  // Stats channels that must be non-null for this scenario to produce results
  required_channels: Array<
    keyof Pick<
      MeasurementStats,
      | "electricity_kwh"
      | "heat_kwh"
      | "co2_ppm"
      | "indoor_temperature"
      | "water_m3"
    >
  >;
  default_params: Record<string, number>;
  param_labels: Record<string, { label: string; unit: string }>;
  // Rough savings factor used for mock result generation
  mock_savings_pct: number;
  // Which energy channel produces the savings
  savings_channel: "electricity_kwh" | "heat_kwh";
}

export const SIMULATION_SCENARIOS: ScenarioDefinition[] = [
  {
    id: "co2_demand_ventilation",
    name_fi: "Tarpeenmukainen ilmanvaihto (CO₂)",
    description_fi:
      "Mitä jos ilmanvaihto olisi säätynyt CO₂-pitoisuuden mukaan? " +
      "Ventiloidaan vähemmän kun tilat ovat tyhjillään tai CO₂-taso on matala.",
    supported_equipment_types: ["air_handling_unit", "chilled_beam"],
    required_channels: ["co2_ppm", "electricity_kwh"],
    default_params: { co2_threshold_ppm: 800, reduction_pct: 30 },
    param_labels: {
      co2_threshold_ppm: { label: "CO₂-raja", unit: "ppm" },
      reduction_pct: {
        label: "Ilmavirran alennus poissaolon aikana",
        unit: "%",
      },
    },
    mock_savings_pct: 0.18,
    savings_channel: "electricity_kwh",
  },
  {
    id: "night_setback",
    name_fi: "Yöaikainen lämpötilan alennus",
    description_fi:
      "Mitä jos lämpötila olisi laskettu yöaikana ja viikonloppuisin " +
      "käyttämättömyyden aikana? Pienentää lämmitysenergian kulutusta.",
    supported_equipment_types: [
      "heat_pump",
      "boiler",
      "district_heating_substation",
      "heat_exchanger",
    ],
    required_channels: ["heat_kwh", "indoor_temperature"],
    default_params: {
      setback_c: 2,
      setback_hours_start: 22,
      setback_hours_end: 6,
    },
    param_labels: {
      setback_c: { label: "Alennus", unit: "°C" },
      setback_hours_start: { label: "Alkaa klo", unit: "h" },
      setback_hours_end: { label: "Päättyy klo", unit: "h" },
    },
    mock_savings_pct: 0.12,
    savings_channel: "heat_kwh",
  },
  {
    id: "electricity_price_optimization",
    name_fi: "Sähkön hintaoptimointi",
    description_fi:
      "Mitä jos sähkönkulutus olisi siirretty halvempiin tunteihin? " +
      "Lämmitys tai jäähdytys olisi esilämmittänyt/jäähdyttänyt ennen kalliita tunteja.",
    supported_equipment_types: [
      "heat_pump",
      "district_heating_substation",
      "boiler",
      "air_handling_unit",
      "chiller",
    ],
    required_channels: ["electricity_kwh", "heat_kwh"],
    default_params: { flexibility_hours: 3, min_price_ratio: 1.5 },
    param_labels: {
      flexibility_hours: { label: "Jousto-ikkuna", unit: "h" },
      min_price_ratio: { label: "Hintaero aktivointiin", unit: "×" },
    },
    mock_savings_pct: 0.09,
    savings_channel: "electricity_kwh",
  },
  {
    id: "building_usage_setback",
    name_fi: "Käyttöaikainen optimointi",
    description_fi:
      "Mitä jos ilmanvaihto ja lämmitys olisivat skaalattu alas käyttämättömyyden aikana? " +
      "CO₂-taso toimii käyttöasteen proxy-mittarina.",
    supported_equipment_types: [
      "air_handling_unit",
      "heat_pump",
      "boiler",
      "district_heating_substation",
    ],
    required_channels: ["electricity_kwh", "co2_ppm"],
    default_params: { unoccupied_hours_per_day: 10, setback_pct: 25 },
    param_labels: {
      unoccupied_hours_per_day: { label: "Tyhjillään h/vrk", unit: "h" },
      setback_pct: { label: "Alennus poissaolon aikana", unit: "%" },
    },
    mock_savings_pct: 0.14,
    savings_channel: "electricity_kwh",
  },
  {
    id: "weather_preconditioning",
    name_fi: "Sääennusteoptimointi",
    description_fi:
      "Mitä jos rakennus olisi esilämmitetty tai -jäähdytetty ennen ääriolosuhteita? " +
      "Hyödyntää sääennustetta ja rakennuksen termistä massaa.",
    supported_equipment_types: [
      "heat_pump",
      "district_heating_substation",
      "boiler",
      "chiller",
    ],
    required_channels: ["heat_kwh"],
    default_params: { forecast_horizon_h: 4, preconditioning_magnitude_c: 1.5 },
    param_labels: {
      forecast_horizon_h: { label: "Ennustehorisontti", unit: "h" },
      preconditioning_magnitude_c: {
        label: "Esilämmityksen suuruus",
        unit: "°C",
      },
    },
    mock_savings_pct: 0.08,
    savings_channel: "heat_kwh",
  },
  {
    id: "demand_response",
    name_fi: "Kuormanjousto",
    description_fi:
      "Mitä jos huippukuormia olisi siirretty tai leikattu verkon ruuhka-aikoina? " +
      "Vähentää tehomaksuja ja mahdollistaa joustotuloja.",
    supported_equipment_types: [
      "heat_pump",
      "chiller",
      "air_handling_unit",
      "battery_system",
    ],
    required_channels: ["electricity_kwh"],
    default_params: { events_per_month: 6, reduction_pct: 20 },
    param_labels: {
      events_per_month: { label: "Joustotapahtumia / kk", unit: "kpl" },
      reduction_pct: { label: "Kuormanalennus tapahtumassa", unit: "%" },
    },
    mock_savings_pct: 0.07,
    savings_channel: "electricity_kwh",
  },
];

export function optimizationColor(pct: number): string {
  if (pct >= 30) return "#e05c5c";
  if (pct >= 15) return "#f5a623";
  return "#4caf82";
}
